import React, { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import ThreadMessages from "@/components/ThreadMessages";
import { ArrowLeft, CheckCircle2, XCircle, Clock, Calendar, User } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { LeavePeriod } from "@/components/requests/LeavePeriod";
import { useAuth, isStudent } from "@/lib/auth";
import { RequestAttachments } from "@/components/requests/RequestAttachments";
import { RequestAssign } from "@/components/requests/RequestAssign";

const CAT_LABEL = {
  permesso_assenza: "Permesso assenza",
  certificato: "Certificato",
  prenotazione_esame: "Prenotazione esame",
  colloquio: "Colloquio",
  generica: "Generica",
};

const STATUS = {
  pending: { label: "In attesa", cls: "bg-amber-100 text-amber-800", icon: Clock },
  approvato: { label: "Approvata", cls: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
  rifiutato: { label: "Rifiutata", cls: "bg-red-100 text-red-800", icon: XCircle },
};

function LeaveHoursBox({ req, editable, hours, onHours, hoursValue }) {
  const q = req.quota;
  const approved = req.status === "approvato";
  const before = q ? (approved ? q.remaining_hours + Number(req.requested_hours || 0) : q.remaining_hours) : null;
  const after = before != null ? before - hoursValue : null;
  return (
    <div className="mt-5 p-3 rounded-lg border border-stone-200 bg-stone-50 text-sm space-y-2" data-testid="leave-hours-box">
      <div className="text-[10px] uppercase tracking-widest text-stone-500">Monte ore permessi</div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-stone-600">Ore {approved ? "scalate" : "richieste"}</span>
        {editable ? (
          <Input type="number" min="0" step="0.5" value={hours === "" ? String(req.requested_hours || 0) : hours}
            onChange={(e) => onHours(e.target.value)} className="h-8 w-24 text-right bg-white" data-testid="decide-hours"/>
        ) : <b data-testid="leave-hours-requested">{req.requested_hours || 0}h</b>}
      </div>
      {q && (
        <>
          <div className="flex justify-between"><span className="text-stone-600">Rimaste {approved ? "prima" : "oggi"}</span><b data-testid="leave-hours-before">{before}h</b></div>
          <div className="flex justify-between"><span className="text-stone-600">Rimaste {approved ? "dopo l'approvazione" : "se approvi"}</span>
            <b className={after < 0 ? "text-red-600" : "text-[#1C352D]"} data-testid="leave-hours-after">{after}h</b></div>
          <div className="text-[11px] text-stone-500">Totale {q.total_hours}h</div>
        </>
      )}
      {editable && <div className="text-[11px] text-stone-500">Puoi correggere le ore prima di approvare (es. permesso di 3 ore senza orario).</div>}
    </div>
  );
}

export default function RequestDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const student = isStudent(user);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [decision, setDecision] = useState(null); // "approvato" | "rifiutato"
  const [note, setNote] = useState("");
  const [hours, setHours] = useState("");

  const backPath = student ? "/portale/richieste" : "/richieste";

  const { data: req, refetch } = useQuery({
    queryKey: ["request", id],
    queryFn: async () => (await api.get(`/requests/${id}`)).data,
    refetchInterval: 15_000,
  });

  const decideMut = useMutation({
    mutationFn: async (vars) => (await api.patch(`/requests/${id}/decide`, { decision: vars.decision, note: vars.note, hours: vars.hours })).data,
    onSuccess: (_data, vars) => {
      toast.success(`Richiesta ${vars.decision === "approvato" ? "approvata" : "rifiutata"}`);
      qc.invalidateQueries({ queryKey: ["request", id] });
      qc.invalidateQueries({ queryKey: ["requests"] });
      qc.invalidateQueries({ queryKey: ["requests-stats"] });
      qc.invalidateQueries({ queryKey: ["requests-stats-badge"] });
      setDecision(null); setNote(""); setHours("");
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  if (!req) return <div className="p-10 text-stone-500 text-sm">Caricamento…</div>;

  const isLeave = req.category === "permesso_assenza";
  const hoursValue = hours === "" ? Number(req.requested_hours || 0) : Number(hours);
  const St = STATUS[req.status] || STATUS.pending;
  const StIcon = St.icon;

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1400px] mx-auto">
      <Link to={backPath} className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-[#1C352D] mb-4">
        <ArrowLeft size={13}/> Torna all&apos;elenco
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-4">
          <Card className="p-6 border border-stone-200 rounded-xl shadow-none">
            <Badge className={`rounded-full text-[10px] ${St.cls}`}><StIcon size={11} className="mr-1"/> {St.label}</Badge>
            <h1 className="text-xl font-semibold text-[#1C352D] mt-3">{req.subject}</h1>
            <div className="text-[11px] text-stone-500 mt-1">{req.medical ? "Visita medica / Sanità" : CAT_LABEL[req.category]}</div>

            <div className="mt-5 space-y-2.5 text-sm border-t border-stone-100 pt-4">
              {!student && (
                <div className="flex gap-2 items-center">
                  <User size={13} className="text-stone-400"/>
                  <div>
                    <div className="font-medium">{req.student_name}</div>
                    <div className="text-[11px] text-stone-500">{req.student_matricola} · {req.student_email}</div>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2 text-stone-600">
                <Calendar size={13} className="text-stone-400"/>
                <span>Creata il {fmtDate(req.created_at)}</span>
              </div>
              {req.date_from && <LeavePeriod req={req} />}
              {req.cert_type && (
                <div className="text-sm"><span className="text-stone-500">Tipo certificato:</span> {req.cert_type}</div>
              )}
              {req.preferred_date && (
                <div className="text-sm"><span className="text-stone-500">Data preferita:</span> {fmtDate(req.preferred_date)}</div>
              )}
            </div>

            {isLeave && req.medical && (
              <div className="mt-5 p-3 rounded-lg border border-sky-200 bg-sky-50 text-sm" data-testid="medical-hours-box">
                <div className="text-[10px] uppercase tracking-widest text-sky-800">Visita medica</div>
                <div className="mt-1 text-sky-900"><b>{req.requested_hours || 0}h</b> · non scala dal monte ore permessi</div>
              </div>
            )}
            {isLeave && req.medical && req.attachments && <RequestAttachments req={req} />}
            {isLeave && !req.medical && (
              <LeaveHoursBox
                req={req}
                editable={!student && req.status === "pending"}
                hours={hours}
                onHours={setHours}
                hoursValue={hoursValue}
              />
            )}

            {req.status !== "pending" && req.decision_note && (
              <div className="mt-5 p-3 bg-stone-50 rounded-lg border-l-4 border-[#1C352D]">
                <div className="text-[10px] uppercase tracking-widest text-stone-500">Nota della segreteria</div>
                <div className="text-sm mt-1">{req.decision_note}</div>
                {req.decided_by_name && <div className="text-[11px] text-stone-500 mt-2">— {req.decided_by_name}</div>}
              </div>
            )}
          </Card>

          {!student && <RequestAssign req={req} user={user}/>}

          {!student && req.status === "pending" && (
            <Card className="p-5 border border-stone-200 rounded-xl shadow-none space-y-3">
              <div className="text-[10px] uppercase tracking-widest text-stone-500">Decidi</div>
              <Textarea
                data-testid="decide-note"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Nota opzionale per lo studente…"
                className="bg-white"
              />
              <div className="grid grid-cols-2 gap-2">
                <Button
                  data-testid="decide-approve"
                  onClick={() => setDecision("approvato")}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full"
                >
                  <CheckCircle2 size={14} className="mr-1.5"/> Approva
                </Button>
                <Button
                  data-testid="decide-reject"
                  onClick={() => setDecision("rifiutato")}
                  variant="outline"
                  className="border-red-300 text-red-700 hover:bg-red-50 rounded-full"
                >
                  <XCircle size={14} className="mr-1.5"/> Rifiuta
                </Button>
              </div>
            </Card>
          )}
        </div>

        <div className="lg:col-span-2">
          <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-3">Conversazione</div>
            <ThreadMessages
              endpoint="requests"
              resourceId={id}
              messages={req.messages}
              refetch={refetch}
            />
          </Card>
        </div>
      </div>

      <AlertDialog open={!!decision} onOpenChange={(v) => !v && setDecision(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confermi di {decision === "approvato" ? "approvare" : "rifiutare"} questa richiesta?</AlertDialogTitle>
            <AlertDialogDescription>
              {decision === "approvato" && isLeave && req.medical && <>Visita medica: nessuna ora scalata dal monte ore; lo studente risulterà giustificato nelle presenze. </>}
              {decision === "approvato" && isLeave && !req.medical && <>Verranno scalate <b>{hoursValue}h</b> dal monte ore dello studente. </>}
              Lo studente riceverà una notifica {note ? "con la tua nota" : ""}. L&apos;azione è tracciata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="decide-confirm"
              className={decision === "approvato" ? "bg-emerald-700 hover:bg-emerald-800 text-white" : "bg-red-600 hover:bg-red-700 text-white"}
              onClick={() => decideMut.mutate({ decision, note, hours: isLeave && !req.medical && hours !== "" ? Number(hours) : undefined })}
            >
              Conferma {decision === "approvato" ? "approvazione" : "rifiuto"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
